import { useEffect, useState } from 'react';
import {
  OwcTable,
  OwcTableHeader,
  OwcTableHeaderCell,
  OwcTableBody,
  OwcTableRow,
  OwcTableCell,
  OwcButton,
  OwcInput,
  OwcIcon,
  OwcSkeletonLoader,
  OwcProgressBar,
} from '@one/react';
import { useGroupData } from '../../contexts/GroupDataContext';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useGetUntaggedQuery, useCreateReleaseMutation } from '../../rtk/api/datagroupsApi';

const isVersionGreater = (userVersion, currentVersion) => {
  const u = userVersion.split('.').map(Number);
  const c = currentVersion.split('.').map(Number);
  const [uMajor, uMinor, uPatch] = [...u, 0, 0].slice(0, 3);
  const [cMajor, cMinor, cPatch] = [...c, 0, 0].slice(0, 3);
  if (uMajor > cMajor) return true;
  if (uMajor < cMajor) return false;
  if (uMinor > cMinor) return true;
  if (uMinor < cMinor) return false;
  return uPatch > cPatch;
};

export default function ReleaseTabs({ onClose, onReleaseCreated }) {
  const [activeTab, setActiveTab] = useState('events');
  const [version, setVersion] = useState('');
  const [comment, setComment] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [selectedEventIndexes, setSelectedEventIndexes] = useState([]);
  const [selectedResourceIndexes, setSelectedResourceIndexes] = useState([]);
  const [selectedComplexIndexes, setSelectedComplexIndexes] = useState([]);
  const [isVersionInvalid, setIsVersionInvalid] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');

  const { latestVersion, setLatestVersion, groupData } = useGroupData();
  const groupName = groupData?.groupName;
  const auth = useAuth();
  const navigate = useNavigate();

  // ── RTK Query: fetch untagged ──
  const {
    data: untaggedData,
    isLoading: loading,
    error: fetchErrorObj,
  } = useGetUntaggedQuery({ groupName }, { skip: !groupName || !auth?.user?.id_token });

  const events = untaggedData?.events || [];
  const resources = untaggedData?.resources || [];
  const complexDataTypes = untaggedData?.complexDataTypes || [];

  const fetchError = fetchErrorObj
    ? fetchErrorObj.message === 'Failed to fetch'
      ? '❌ CORS error. Failed to load events/resources.'
      : 'Failed to load events/resources. Please try again later.'
    : null;

  // ── RTK Query: create release ──
  const [createRelease, { isLoading: submitting }] = useCreateReleaseMutation();

  // pick default tab once data has arrived
  useEffect(() => {
    if (!loading && !fetchError) {
      if (events.length > 0) setActiveTab('events');
      else if (resources.length > 0) setActiveTab('resources');
      else if (complexDataTypes.length > 0) setActiveTab('complex');
    }
  }, [loading, events, resources, complexDataTypes, fetchError]);

  // clear when modal finally unmounts
  useEffect(() => {
    return () => {
      setSelectedEventIndexes([]);
      setSelectedResourceIndexes([]);
      setSelectedComplexIndexes([]);
      setVersion('');
      setComment('');
      setStatusMessage('');
    };
  }, []);

  const checkDuplicates = (data, indexes, type) => {
    const keys = indexes.map((i) => {
      const item = data[i];
      const name = type === 'complex' ? item.cdtName : item.eventName || item.resourceName;
      const hierarchy = item.hierarchy || '';
      return `${name}|${hierarchy}`;
    });
    const setKeys = new Set(keys);
    return setKeys.size !== keys.length;
  };

  const handleVersionInput = (e) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    const validPattern = /^(\d+(\.\d{0,2}){0,2})?$/;
    setVersion(val);
    setIsVersionInvalid(
      !val || !validPattern.test(val)
        ? true
        : latestVersion
          ? !isVersionGreater(val, latestVersion)
          : false
    );
  };

  const handleSelectedRowsChange = (indexes, type) => {
    if (type === 'events') setSelectedEventIndexes(indexes);
    else if (type === 'resources') setSelectedResourceIndexes(indexes);
    else if (type === 'complex') setSelectedComplexIndexes(indexes);

    let hasDup = false;
    if (type === 'events') hasDup = checkDuplicates(events, indexes, 'events');
    else if (type === 'resources') hasDup = checkDuplicates(resources, indexes, 'resources');
    else if (type === 'complex') hasDup = checkDuplicates(complexDataTypes, indexes, 'complex');

    setDuplicateError(
      hasDup ? '❌ Duplicate Event/Resource selected — please deselect duplicates.' : ''
    );
  };

  const extractSelected = (list, indexes, type) =>
    indexes.map((i) => {
      const item = list[i];
      const key = type === 'complex' ? item.cdtName : item.eventName || item.resourceName;
      return { name: key, hierarchy: item.hierarchy || '', version: item.version || '' };
    });

  const getSelectionPayload = () => ({
    releaseVersion: version,
    comment: comment || '',
    createdBy: auth?.user?.profile?.email || '',
    events: extractSelected(events, selectedEventIndexes, 'events'),
    resources: extractSelected(resources, selectedResourceIndexes, 'resources'),
    complexDataTypes: extractSelected(complexDataTypes, selectedComplexIndexes, 'complex'),
  });

  const handleCreateRelease = async () => {
    setStatusMessage('');
    try {
      const result = await createRelease({
        groupName,
        payload: getSelectionPayload(),
      }).unwrap();

      setLatestVersion(version);
      setStatusMessage(`✅ Release created successfully for version ${version}`);
      setSelectedEventIndexes([]);
      setSelectedResourceIndexes([]);
      setSelectedComplexIndexes([]);
      setVersion('');
      setComment('');
      onReleaseCreated?.();
      onReleaseCreated?.();
      navigate('/dashboard');
      window.dispatchEvent(new CustomEvent('refreshReleases'));
    } catch (err) {
      console.error(err);
      setStatusMessage(`❌ ${err.data?.detail || err.message || 'Failed to create release'}`);
    }
  };

  const renderTable = (data, selected, type) => (
    <OwcTable
      verticalBorder
      stickyHeader
      size="small"
      showSelectionColumn
      selectedRows={selected}
      onSelectedRowsChange={(e) => handleSelectedRowsChange(e.detail, type)}
      style={{ fontSize: '6px' }}
    >
      <OwcTableHeader style={{ fontSize: '11px' }}>
        <OwcTableHeaderCell width="35%">Name</OwcTableHeaderCell>
        <OwcTableHeaderCell width="30%">Hierarchy</OwcTableHeaderCell>
        <OwcTableHeaderCell width="90px">Status</OwcTableHeaderCell>
        <OwcTableHeaderCell>Version</OwcTableHeaderCell>
      </OwcTableHeader>
      <OwcTableBody style={{ fontSize: '11px' }}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <OwcTableRow key={i} selectable>
                {[80, 70, 50, 30].map((w, j) => (
                  <OwcTableCell key={j}>
                    <OwcSkeletonLoader variant="text" style={{ width: `${w}%` }} />
                  </OwcTableCell>
                ))}
              </OwcTableRow>
            ))
          : data.map((item, idx) => {
              let name, status;
              if (type === 'complex') {
                name = item.cdtName;
                status = (item.status || '').toLowerCase();
              } else {
                name = type === 'events' ? item.eventName : item.resourceName;
                status = (item.status || '').toLowerCase();
              }
              return (
                <OwcTableRow key={idx} selectable>
                  <OwcTableCell>{name}</OwcTableCell>
                  <OwcTableCell>{item.hierarchy || '—'}</OwcTableCell>
                  <OwcTableCell
                    style={{
                      color:
                        status === 'added'
                          ? '#004080'
                          : status === 'modified'
                            ? 'green'
                            : 'inherit',
                      fontWeight: 'bold',
                    }}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </OwcTableCell>
                  <OwcTableCell>{item.version || '—'}</OwcTableCell>
                </OwcTableRow>
              );
            })}
      </OwcTableBody>
    </OwcTable>
  );

  const noData =
    !loading && events.length === 0 && resources.length === 0 && complexDataTypes.length === 0;
  const noSelect =
    selectedEventIndexes.length + selectedResourceIndexes.length + selectedComplexIndexes.length ===
    0;
  const hasEventOrResource = selectedEventIndexes.length > 0 || selectedResourceIndexes.length > 0;
  const canCreate =
    !noData &&
    version &&
    !isVersionInvalid &&
    !noSelect &&
    comment.trim() &&
    hasEventOrResource &&
    !duplicateError;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', fontSize: '13px', padding: 0 }}>
      {/* Tabs */}
      <div style={{ display: 'flex', marginBottom: 12, borderBottom: '1px solid #e0e0e0' }}>
        {['events', 'resources', 'complex'].map((tab) => {
          const count =
            tab === 'events'
              ? events.length
              : tab === 'resources'
                ? resources.length
                : complexDataTypes.length;
          const label =
            tab === 'complex' ? 'ComplexDataTypes' : tab.charAt(0).toUpperCase() + tab.slice(1);
          return (
            <button
              key={tab}
              disabled={count === 0}
              onClick={() => setActiveTab(tab)}
              style={{
                fontWeight: activeTab === tab ? 'bold' : 'normal',
                borderBottom: activeTab === tab ? '2px solid #1976d2' : '2px solid transparent',
                padding: '6px 16px 6px 0',
                marginRight: 16,
                background: 'transparent',
                border: 'none',
                cursor: count === 0 ? 'not-allowed' : 'pointer',
                opacity: count === 0 ? 0.5 : 1,
              }}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>
      {/* Table / Error / Fallback */}
      <div style={{ maxHeight: '280px', overflowY: 'auto', marginBottom: '16px' }}>
        {fetchError ? (
          <div style={{ color: 'red', padding: '8px 12px', fontSize: '13px' }}>{fetchError}</div>
        ) : noData ? (
          <div style={{ padding: '8px 12px', fontSize: '13px' }}>
            Event / Resource / ComplexDataType not available
          </div>
        ) : (
          <>
            <div style={{ display: activeTab === 'events' ? 'block' : 'none' }}>
              {renderTable(events, selectedEventIndexes, 'events')}
            </div>
            <div style={{ display: activeTab === 'resources' ? 'block' : 'none' }}>
              {renderTable(resources, selectedResourceIndexes, 'resources')}
            </div>
            <div style={{ display: activeTab === 'complex' ? 'block' : 'none' }}>
              {renderTable(complexDataTypes, selectedComplexIndexes, 'complex')}
            </div>
          </>
        )}
      </div>
      {/* Inputs + Create */}
      {!fetchError && !noData && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
            <OwcInput
              placeholder="Enter release version"
              value={version}
              size="small"
              onInput={handleVersionInput}
              no-clear-icon
            />
            {duplicateError && (
              <div style={{ color: 'red', fontSize: 12, marginBottom: 6 }}>{duplicateError}</div>
            )}
            <OwcButton
              variant="primary"
              size="medium"
              disabled={!canCreate}
              onClick={handleCreateRelease}
            >
              Create release
            </OwcButton>
          </div>
          <textarea
            placeholder="Enter comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            style={{
              flex: 2,
              resize: 'none',
              padding: 10,
              borderRadius: 4,
              fontSize: 13,
              border: '1px solid #ccc',
              fontFamily: 'inherit',
              height: 65,
            }}
          />
        </div>
      )}
      {/* Loader + Status */}
      <div style={{ minHeight: 0, marginTop: 12 }}>
        {submitting ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <OwcProgressBar size="medium" color="info" indeterminate />
            <div style={{ fontSize: 12, color: '#555' }}>creating new release please wait...</div>
          </div>
        ) : (
          <div
            style={{
              fontSize: 13,
              color: statusMessage.includes('❌') ? 'red' : 'green',
              whiteSpace: 'pre-wrap',
            }}
          >
            {statusMessage}
          </div>
        )}
      </div>
      {(events.length > 0 || resources.length > 0 || complexDataTypes.length > 0) && (
        <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>
          <OwcIcon name="info" family="outlined" style={{ marginRight: 4 }} />
          <strong>Note:</strong>
          <ul style={{ marginTop: 6, paddingLeft: 18 }}>
            <li>
              At least one <strong>event</strong> or <strong>resource</strong> is required, along
              with a release version and comment — <strong>all fields are mandatory</strong>.
            </li>
            <li>
              The entered release version must be higher than the current version (
              <strong>{latestVersion || '—'}</strong>).
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
